/* eslint-disable react/no-array-index-key */
import React, { useEffect, useState } from 'react';
import { Csv, Mapping } from '../csv';
import Button from '@teetsh/app/src/components/Buttons/Button';
import 'twin.macro';
import { FieldMappingOptions } from '@teetsh/app/src/containers/Eleves/Import/File/FieldMappingOptions';
import { useTranslation, Trans } from 'react-i18next';
import { Checkbox } from 'components/Form/Checkboxes/Checkbox';
import useClassrooms from 'containers/App/context/ClassroomsContext/useClassrooms';
import { Infobox } from '@teetsh/app/src/components/Callout/Infobox';
import {
  ExclamationTriangleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

interface Props {
  csv: Csv | null;
  mappings: Mapping[] | null;
  setMappings: (v: Mapping[]) => void;
  onResetToUpload?: () => void;
}

export function FieldMappings(props: Props) {
  const { csv, setMappings: propsSetMappings, onResetToUpload } = props;
  const classrooms = useClassrooms();

  const [mappings, setMappings] = useState(props.mappings);
  useEffect(() => {
    setMappings(props.mappings);
  }, [props.mappings]);
  const { t } = useTranslation(['common', 'eleve']);
  const hasGivenName =
    (mappings || []).filter(m => m.checked && m.mapping === 'givenName')
      .length > 0;
  const hasFamilyName =
    (mappings || []).filter(m => m.checked && m.mapping === 'familyName')
      .length > 0;
  const isValid = hasGivenName && hasFamilyName;
  const hasRows = (csv?.rows || []).length > 0;

  const hasLSUClassroom = classrooms.some(
    c => c.cycle.includes('Cycle 2') || c.cycle.includes('Cycle 3'),
  );
  const hasINE =
    (mappings || []).filter(m => m.checked && m.mapping === 'ine').length > 0;
  const shouldShowINEWarning = hasLSUClassroom && !hasINE;
  const [isINEWarningDismissed, setIsINEWarningDismissed] = useState(false);
  const showINEWarning = shouldShowINEWarning && !isINEWarningDismissed;

  return (
    <div tw="flex flex-col gap-y-4">
      <div tw="flex flex-col gap-y-4">
        {hasRows && (
          <div>
            <div tw="grid grid-cols-1 md:grid-cols-3 gap-20 items-center mb-4 text-neutral-quiet pl-4 pr-6">
              <div tw="flex gap-4 items-center w-[275px]">
                <div tw="w-4"></div> {/* Space for checkbox */}
                <div>{t('eleve:import.fileHeader')}</div>
              </div>
              <div tw="w-[140px]">{t('eleve:import.someData')}</div>
              <div tw="flex-1">{t('eleve:import.eleveAttributes')}</div>
            </div>

            <div tw="flex flex-col gap-y-4">
              {(mappings || []).map((mapping, mappingIndex) => (
                <div
                  tw="bg-neutral-supershy rounded-lg flex flex-col gap-4 md:grid md:grid-cols-3 md:gap-20 md:items-center pl-4 pr-6 py-4"
                  key={mapping.index}
                >
                  <div tw="flex gap-4 items-center w-[275px]">
                    <Checkbox
                      checked={mapping.checked}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>,
                      ) => {
                        setMappings(
                          (mappings || []).map(m =>
                            m.index === mappingIndex
                              ? { ...m, checked: event.target.checked }
                              : m,
                          ),
                        );
                      }}
                    />
                    <div tw="text-neutral-intense">{mapping.header}</div>
                  </div>
                  <div tw="italic text-neutral-intense w-[140px]">
                    {(csv?.rows || []).slice(0, 2).map((row, i) => (
                      <div key={i}>{row[mapping.header]}</div>
                    ))}
                  </div>
                  <div tw="flex-1">
                    <FieldMappingOptions
                      value={mapping.mapping}
                      mappings={mappings}
                      onChange={v =>
                        setMappings(
                          (mappings || []).map(m =>
                            m.index === mappingIndex ? { ...m, mapping: v } : m,
                          ),
                        )
                      }
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {showINEWarning && (
          <div>
            <Infobox
              intent="warning"
              icon={ExclamationTriangleIcon}
              title={t('eleve:import.missingLSUDataTitle')}
              onClose={() => setIsINEWarningDismissed(true)}
              actions={
                <div tw="flex justify-end gap-2.5">
                  <Button
                    outlined
                    intent="warning"
                    onClick={() =>
                      window.open(
                        'https://teetsh.com/documentation/livrets/',
                        '_blank',
                      )
                    }
                  >
                    {t('eleve:import.aboutButton')}
                  </Button>
                  <Button intent="warning" onClick={onResetToUpload}>
                    {t('eleve:import.modifyListButton')}
                  </Button>
                </div>
              }
            >
              <div>
                <Trans
                  i18nKey="eleve:import.missingLSUDataDescription"
                  components={{
                    1: <strong />,
                    3: <strong />,
                    5: <br />,
                  }}
                />
              </div>
            </Infobox>
          </div>
        )}

        {!isValid && (
          <div>
            <Infobox
              intent="danger"
              layout="inline"
              icon={XCircleIcon}
              title={t('eleve:import.incompleteFileTitle')}
              actions={
                <div tw="flex justify-end">
                  <Button intent="danger" onClick={onResetToUpload}>
                    {t('eleve:import.modifyFileButton')}
                  </Button>
                </div>
              }
            >
              <div>{t('eleve:import.incompleteFileDescription')}</div>
            </Infobox>
          </div>
        )}
        {!hasRows && (
          <div>
            <Infobox
              intent="danger"
              layout="inline"
              icon={XCircleIcon}
              title={t('eleve:import.emptyRowsTitle')}
              actions={
                <div tw="flex justify-end">
                  <Button intent="danger" onClick={onResetToUpload}>
                    {t('eleve:import.modifyFileButton')}
                  </Button>
                </div>
              }
            >
              <div>{t('eleve:import.emptyRowsDescription')}</div>
            </Infobox>
          </div>
        )}
      </div>
      {mappings && (
        <div tw="flex justify-end">
          <Button
            disabled={!isValid || !hasRows}
            intent="primary"
            onClick={() => {
              const checkedMappings = mappings?.filter(m => m.checked) || [];
              propsSetMappings(checkedMappings);
            }}
          >
            {t('common:buttons.next')}
          </Button>
        </div>
      )}
    </div>
  );
}
